/**
 * Feb 2026 — visual badge for the Zoho push reconciler lifecycle. Used
 * in list views (Dispatch / RM Inward Bills / Delivery Challan) so the
 * operator can tell at a glance which rows succeeded, are mid-flight,
 * failed, or have exhausted their retry budget.
 *
 * Inputs:
 *   status        — string | undefined from `zoho_push_status` (or, for
 *                   the IBT-linked DC, `zoho_dc_push_status`).
 *   error         — optional string from `zoho_push_error` (tooltip).
 *   retryCount    — number from `zoho_push_retry_count` (badge subtitle).
 *   maxAttempts   — defaults to 3; passed through so the badge matches
 *                   whatever the backend's `_MAX_ATTEMPTS` is.
 *
 * When `status` is null/undefined/COMPLETED the badge renders nothing —
 * the row is "happy", no visual noise needed.
 */
import { AlertTriangle, Clock, XCircle } from "lucide-react";

const _STATUS_STYLES = {
  PENDING: {
    icon: Clock,
    label: "Zoho push pending",
    className: "bg-amber-100 text-amber-900 border-amber-300",
  },
  PENDING_UPDATE: {
    icon: Clock,
    label: "Zoho update pending",
    className: "bg-amber-100 text-amber-900 border-amber-300",
  },
  FAILED: {
    icon: AlertTriangle,
    label: "Zoho push failed — will retry",
    className: "bg-red-100 text-red-900 border-red-300",
  },
  EXHAUSTED: {
    icon: XCircle,
    label: "Zoho push exhausted — manual fix needed",
    className: "bg-red-200 text-red-950 border-red-400",
  },
};

export function ZohoPushStatusBadge({
  status,
  error,
  retryCount = 0,
  maxAttempts = 3,
  testIdPrefix = "zoho-push",
}) {
  if (!status || status === "COMPLETED") return null;
  const cfg = _STATUS_STYLES[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const suffix =
    status === "FAILED" || status === "EXHAUSTED"
      ? ` (${retryCount}/${maxAttempts})`
      : "";
  const tooltip = error
    ? `${cfg.label}${suffix}: ${error}`
    : `${cfg.label}${suffix}`;
  return (
    <span
      title={tooltip}
      data-testid={`${testIdPrefix}-badge-${status.toLowerCase()}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.className}`}
    >
      <Icon className="w-3 h-3" />
      <span>{status === "EXHAUSTED" ? "Exhausted" : status === "FAILED" ? `Failed${suffix}` : "Pushing…"}</span>
    </span>
  );
}

export default ZohoPushStatusBadge;
